import { useEffect, useState } from 'react';
import { Loader2, Gift, MailCheck, Search, Sparkles, AlertCircle, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const GOLD = '#D4AF37';

/**
 * Admin tool to grant a "free campaign" credit to producers whose blast went
 * out with the wrong sender domain (or any other incident). One credit
 * bypasses the daily 1-campaign cap once.
 */
export default function AdminCampaignCreditsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [since, setSince] = useState(defaultSince());
  const [submitting, setSubmitting] = useState(false);
  const [granting, setGranting] = useState({}); // {producer_id: true}

  function defaultSince() {
    // Default: 14 days back
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  }

  const load = async (sinceParam = since) => {
    setLoading(true);
    try {
      const sinceIso = sinceParam ? new Date(`${sinceParam}T00:00:00Z`).toISOString() : null;
      const r = await api.get(`/admin/email-campaigns/recent?limit=200${sinceIso ? `&since=${sinceIso}` : ''}`);
      setCampaigns(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('No se pudo cargar el listado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const sinceIso = since ? new Date(`${since}T00:00:00Z`).toISOString() : null;
      try {
        const r = await api.get(`/admin/email-campaigns/recent?limit=200${sinceIso ? `&since=${sinceIso}` : ''}`);
        if (mounted) setCampaigns(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (mounted) toast.error('No se pudo cargar el listado');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [since]);

  const grantOne = async (producerId) => {
    setGranting((p) => ({ ...p, [producerId]: true }));
    try {
      await api.post('/admin/email-campaigns/grant-credit', { producer_id: producerId, credits: 1 });
      toast.success('Crédito otorgado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo otorgar');
    } finally {
      setGranting((p) => ({ ...p, [producerId]: false }));
    }
  };

  const grantBulk = async () => {
    if (!window.confirm(`Vas a darle 1 campaña gratis a TODAS las productoras que enviaron una campaña desde el ${since}. ¿Confirmás?`)) return;
    setSubmitting(true);
    try {
      const sinceIso = new Date(`${since}T00:00:00Z`).toISOString();
      const r = await api.post('/admin/email-campaigns/grant-credit', {
        grant_all_who_sent_since: sinceIso,
        credits: 1,
      });
      const n = (r.data?.granted_to || []).length;
      toast.success(`Crédito otorgado a ${n} productora${n === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo otorgar masivamente');
    } finally {
      setSubmitting(false);
    }
  };

  // Group by producer to show unique productoras
  const byProducer = new Map();
  for (const c of campaigns) {
    if (!c.producer_id) continue;
    const cur = byProducer.get(c.producer_id) || {
      producer_id: c.producer_id,
      producer_name: c.producer_name,
      producer_email: c.producer_email,
      producer_credits: c.producer_credits,
      campaigns: [],
    };
    cur.campaigns.push(c);
    byProducer.set(c.producer_id, cur);
  }
  const list = [...byProducer.values()].filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.producer_name || '').toLowerCase().includes(s)
      || (p.producer_email || '').toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen bg-black text-white p-6 lg:p-10" data-testid="admin-campaign-credits-page">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          Marketing · Créditos
        </p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          Re-otorgar campañas
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Si una campaña salió con el dominio incorrecto o tuvo un error, podés darle a la productora un crédito para que pueda volver a enviar la campaña hoy mismo (sin esperar 24h).
        </p>
      </div>

      {/* Bulk panel */}
      <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 mb-6" data-testid="bulk-grant-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#D4AF37]" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#D4AF37]">Otorgar masivamente</h3>
        </div>
        <p className="text-xs text-zinc-400 mb-4">
          Le da <strong className="text-white">1 crédito de campaña</strong> a <strong className="text-white">todas las productoras</strong> que enviaron al menos 1 campaña desde la fecha indicada.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="block text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Desde</label>
            <Input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="bg-zinc-950 border-zinc-800"
              data-testid="bulk-since-input"
            />
          </div>
          <Button
            onClick={grantBulk}
            disabled={submitting}
            className="bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold"
            data-testid="bulk-grant-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Gift size={14} className="mr-2" />}
            Otorgar a todas (mismo período)
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          placeholder="Buscar productora por nombre o email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-zinc-950 border-zinc-800"
          data-testid="search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-zinc-500" /></div>
      ) : list.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3" data-testid="producers-list">
          {list.map((p) => (
            <ProducerCard
              key={p.producer_id}
              producer={p}
              onGrant={() => grantOne(p.producer_id)}
              granting={!!granting[p.producer_id]}
            />
          ))}
        </div>
      )}

      <button
        onClick={load}
        className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
        data-testid="refresh-btn"
      >
        <RotateCw size={12} /> Refrescar
      </button>
    </div>
  );
}

function ProducerCard({ producer, onGrant, granting }) {
  const lastCampaign = producer.campaigns[0];
  return (
    <div
      className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`producer-credit-row-${producer.producer_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-white truncate">{producer.producer_name || '—'}</p>
          {producer.producer_credits > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-wider"
            >
              <Gift size={10} /> {producer.producer_credits} crédito{producer.producer_credits === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 truncate mt-0.5">{producer.producer_email}</p>
        {lastCampaign && (
          <p className="text-[11px] text-zinc-500 mt-2">
            <MailCheck size={11} className="inline mr-1" />
            Última campaña: <strong className="text-zinc-300">&ldquo;{lastCampaign.subject}&rdquo;</strong>
            <span className="text-zinc-600"> · {lastCampaign.sent}/{lastCampaign.total_recipients} enviados</span>
            <span className="text-zinc-600"> · {new Date(lastCampaign.created_at).toLocaleDateString('es-AR')}</span>
          </p>
        )}
        <p className="text-[11px] text-zinc-600 mt-1">{producer.campaigns.length} campaña{producer.campaigns.length === 1 ? '' : 's'} en el período</p>
      </div>
      <Button
        onClick={onGrant}
        disabled={granting}
        className="bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold w-full sm:w-auto"
        data-testid={`grant-credit-btn-${producer.producer_id}`}
      >
        {granting ? <Loader2 size={13} className="animate-spin mr-2" /> : <Gift size={13} className="mr-2" />}
        Otorgar 1 campaña
      </Button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 rounded-2xl border border-dashed border-zinc-800">
      <AlertCircle size={28} className="mx-auto mb-3 text-zinc-700" />
      <p className="text-sm font-bold text-white">No hay campañas en este período</p>
      <p className="text-xs text-zinc-500 mt-1">Probá expandir el rango de fechas.</p>
    </div>
  );
}
